import { FC, useEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { FormikProps } from 'formik';

import { Flex } from 'src/components/Flex';
import { PrincipalAddressProvider } from 'src/contexts/PrincipalAddressProvider';
import { Card } from 'src/pages/AddPartnerPage/components';
import { Principal } from 'src/types/AddMerchant/merchantInfo';

import PrincipalItem from './components/PrincipalItem';

interface PrincipalComponent {
    formik: FormikProps<any>;
    isNext;
}

export const principalInitialValue: Principal = {
    firstName: '',
    middleNames: '',
    lastName: '',
    title: null,
    dateOfBirth: dayjs().subtract(18, 'years').format('DD/MM/YYYY'),
    nationality: '',
    email: '',
    mobileNumber: '',
    telephoneNumber: '',
    position: '',
    percentageOfOwnership: 0,
    isBeneficialOwner: false,
    isBillingContact: false,
    addresses: [
        {
            country: '',
            type: 'PERSONAL_ADDRESS',
            addressLine1: '',
            addressLine2: '',
            city: '',
            townOrCity: '',
            county: '',
            eircode: '',
            timeAtAddress: {
                years: 0,
                months: 0,
            },
        },
    ],
};

export const PrincipalComponent: FC<PrincipalComponent> = ({ formik, isNext }) => {
    const [selected, setSelected] = useState(0);

    useEffect(() => {
        const principalErrors = formik.errors?.principals;
        if (Array.isArray(principalErrors)) {
            for (let index = 0; index < principalErrors.length; index++) {
                if (principalErrors[index]) {
                    setSelected(index);
                    break;
                }
            }
        }
    }, [isNext]);

    const push = (principal: object) => {
        formik.setFieldValue('principals', [...formik.values.principals, principal]);
        setSelected((previousState) => {
            return previousState + 1;
        });
    };

    const remove = (index: number) => {
        const indexIsSelected = index === selected;

        if (indexIsSelected && index !== 0) {
            setSelected((previousState) => {
                return previousState - 1;
            });
        }

        formik.setFieldValue(
            'principals',
            formik.values.principals.filter((_, i) => {
                return i !== index;
            })
        );
    };

    const data: { heading: string }[] = formik.values.principals.map((_, principalIndex) => {
        return {
            heading: `Principal ${principalIndex + 1}`,
        };
    });

    useEffect(() => {
        if (formik.values.principals.length === 1) {
            formik.setFieldValue('principals.0.isBillingContact', true);
        }
    }, [formik.values.principals]);

    return (
        <PrincipalAddressProvider formik={formik}>
            <Card containerStyle={{ padding: 0, overflow: 'hidden' }}>
                <Flex borderBottom="1px solid #F18F34" justifyContent="flex-start">
                    {data.map(({ heading }, index) => {
                        return (
                            <Box
                                flex={1}
                                key={`heading=${index}`}
                                onClick={() => {
                                    return setSelected(index);
                                }}
                                sx={{
                                    cursor: 'pointer',
                                    ':hover': { bgcolor: '#F18F34', color: '#ffffff' },
                                }}
                                padding="30px 16px"
                                bgcolor={selected === index ? '#F18F34' : 'transparent'}
                                color={selected === index ? '#ffffff' : '#1D1D1B'}
                            >
                                <Typography
                                    fontSize="16px"
                                    fontWeight={selected === index ? '700' : '500'}
                                    textAlign="center"
                                >
                                    {heading}
                                </Typography>
                            </Box>
                        );
                    })}
                </Flex>
                <Box padding="32px">
                    {formik.values.principals
                        .map((principal, principalIndex, principals) => {
                            return (
                                <PrincipalItem
                                    formik={formik}
                                    principal={principal}
                                    principalIndex={principalIndex}
                                    principals={principals}
                                    push={push}
                                    remove={remove}
                                    key={`principal-${principalIndex}`}
                                />
                            );
                        })
                        .find((_, index) => {
                            return index === selected;
                        })}
                </Box>
            </Card>
        </PrincipalAddressProvider>
    );
};
